/**
 * /hospitality/lodging/folio-ar — Guest-folio AR and city-ledger aging.
 *
 * Shows current, 30, 60, 90 and 90+ day buckets for both the guest ledger
 * (in-house folio / late checkout balances) and the city ledger (corporate
 * direct-bill accounts, convention groups).
 */
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useHospitalityPack } from "@/lib/useHospitalityPack";
import { api } from "@/lib/api";
import { PageHeader, StatCard, Panel, money } from "@/components/hospitality/ui";

export const Route = createFileRoute("/_auth/hospitality/lodging/folio-ar")({
  component: FolioARPage,
});

interface FolioARRow {
  ledger_type: string;
  bucket: string;
  patron_ref: string | null;
  company_name: string | null;
  balance: string;
  oldest_charge_date: string | null;
}
interface FolioARResult {
  property: string;
  as_of: string | null;
  items: FolioARRow[];
  by_ledger: Record<string, Record<string, string>>;
  by_bucket: Record<string, string>;
  total: string;
  message?: string;
}

const BUCKETS = ["CURRENT", "30", "60", "90", "90PLUS"];
const BUCKET_LABELS: Record<string, string> = {
  CURRENT: "Current",
  "30": "1–30 days",
  "60": "31–60 days",
  "90": "61–90 days",
  "90PLUS": "90+ days",
};

function FolioARPage() {
  const { enabled } = useHospitalityPack();
  if (!enabled) return null;

  const q = useQuery({
    queryKey: ["hospitality-lodging-folio-ar"],
    queryFn: () => api<FolioARResult>("/api/hospitality/lodging/folio-ar"),
  });

  const data = q.data;

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      <PageHeader
        title="Folio AR & City Ledger"
        subtitle={
          data
            ? `${data.property}${data.as_of ? ` · As of ${data.as_of}` : ""}`
            : "Guest-folio and city-ledger aging"
        }
      />

      <div className="flex-1 min-h-0 overflow-auto px-6 py-4">
        {q.isLoading && (
          <div className="text-sm text-slate-500 dark:text-slate-400">Loading…</div>
        )}

        {!q.isLoading && data?.message && (
          <div className="text-sm text-slate-500 py-12 text-center dark:text-slate-400">
            {data.message}
          </div>
        )}

        {!q.isLoading && data && !data.message && (
          <div className="space-y-4">
            {/* Total + by-bucket summary */}
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2.5">
              <StatCard label="Total AR" value={money(data.total, 0)} />
              {BUCKETS.map((b) => (
                <StatCard
                  key={b}
                  label={BUCKET_LABELS[b]}
                  value={money(data.by_bucket[b] ?? "0.00", 0)}
                />
              ))}
            </div>

            {/* Guest ledger */}
            {data.by_ledger["GUEST"] && (
              <Panel label="Guest ledger">
                <div className="grid grid-cols-5 gap-2 mb-3">
                  {BUCKETS.map((b) => (
                    <div key={b} className="text-center">
                      <div className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-0.5">
                        {BUCKET_LABELS[b]}
                      </div>
                      <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                        {money(data.by_ledger["GUEST"][b] ?? "0.00", 0)}
                      </div>
                    </div>
                  ))}
                </div>
              </Panel>
            )}

            {/* City ledger */}
            {data.by_ledger["CITY"] && (
              <Panel label="City ledger (corporate & convention)">
                <table className="w-full text-sm">
                  <thead className="border-b border-slate-100 dark:border-slate-800 text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    <tr>
                      <th className="text-left py-1.5 pr-3">Account</th>
                      <th className="text-right py-1.5 px-3">Bucket</th>
                      <th className="text-right py-1.5 px-3">Oldest charge</th>
                      <th className="text-right py-1.5 pl-3">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items
                      .filter((r) => r.ledger_type === "CITY")
                      .map((r, i) => (
                        <tr key={i} className="border-t border-slate-100 dark:border-slate-800">
                          <td className="py-1.5 pr-3">
                            <div className="text-slate-800 dark:text-slate-200">
                              {r.company_name ?? r.patron_ref ?? "—"}
                            </div>
                            {r.patron_ref && r.company_name && (
                              <div className="text-xs text-slate-400">{r.patron_ref}</div>
                            )}
                          </td>
                          <td className="py-1.5 px-3 text-right">
                            <span
                              className={`text-xs font-medium px-1.5 py-0.5 rounded ${
                                r.bucket === "90PLUS"
                                  ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"
                                  : r.bucket === "90"
                                  ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
                                  : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
                              }`}
                            >
                              {BUCKET_LABELS[r.bucket] ?? r.bucket}
                            </span>
                          </td>
                          <td className="py-1.5 px-3 text-right tabular-nums text-xs text-slate-500 dark:text-slate-400">
                            {r.oldest_charge_date ?? "—"}
                          </td>
                          <td className="py-1.5 pl-3 text-right tabular-nums text-xs font-medium text-slate-900 dark:text-slate-100">
                            {money(r.balance)}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </Panel>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
